import { LitElement, html, css } from 'lit';

import {
  CSS_SPACING,
  CSS_FONT_SIZE_HEADER,
  CSS_FONT_SIZE_BODY,
  CSS_COLOR_HIGHLIGHT,
  CSS_COLOR_GREY_2,
  CSS_FONT_WEIGHT_BOLD,
} from '../../../../../src/styles';
import {
  getEnterpriseTenants,
  getEnterpriseTenantPermissions,
  deleteEnterpriseTenantPermissions,
  getEnterpriseAdmin,
} from '../../../../neb-api-client/src/enterprise-tenant-permissions-api-client';
import { openError } from '../../../../neb-dialog/neb-banner-state';
import { store } from '../../../../neb-redux/neb-redux-store';
import { COGNITO_TYPES } from '../../../../neb-utils/cognito-util';
import { getSessionState } from '../forms/utils/neb-form-user-util';
import '../../../../../src/components/misc/neb-card-switch';
import '../../../../../src/components/misc/neb-card-select';
import '../../../../../src/components/controls/inputs/neb-select-search-multi';
import '../../../../../src/components/misc/neb-icon';

export const ELEMENTS = {
  container: {
    id: 'container',
  },
  info: {
    id: 'info',
  },
  enterpriseAdmin: {
    id: 'enterprise-admin',
  },
  locationAccess: {
    id: 'location-access',
  },
};

export default class NebEnterpriseAdmin extends LitElement {
  static get properties() {
    return {
      layout: { type: String },
      model: { type: Object },
      errors: { type: Object },
      userId: { type: String },
      enterpriseTenants: { type: Array },
      __loadingTenants: { type: Boolean },
      __loadingPermissions: { type: Boolean },
      __isEnabled: { type: Boolean },
    };
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  static createModel() {
    return {
      enterpriseId: null,
      enterpriseAdmin: false,
      enterpriseTenantPermissions: [],
    };
  }

  static createSelectors() {
    return {
      enterpriseTenantPermissions: {
        unsafe: true,
        clipPristine: true,
        format: v => v || [],
        unformat: v => v || [],
      },
      enterpriseAdmin: {
        unsafe: true,
        clipPristine: true,
        validators: [],
        defaultValue: false,
        format: v => (v === null || v === undefined ? false : !!v),
        unformat: v => (v === null || v === undefined ? false : !!v),
      },
    };
  }

  __handleError(message) {
    store.dispatch(openError(message));
  }

  __initState() {
    this.__enterpriseAdminPermission = false;
    this.layout = 'vertical';
    this.model = null;
    this.errors = {};
    this.userId = null;
    this.__saving = false;
    this.enterpriseTenants = [];
    this.__loadingTenants = false;
    this.__loadingPermissions = false;
    this.__isEnabled = false;

    this.onChange = () => {};

    this.onSave = () => {};

    this.onChangeDirty = () => {};
  }

  async __loadEnterpriseTenants() {
    if (!this.model?.enterpriseId) {
      return;
    }

    try {
      this.__loadingTenants = true;
      const { currentTenantId } = getSessionState();

      const response = await getEnterpriseTenants(
        this.model.enterpriseId,
        currentTenantId,
      );

      const tenants = response.tenants || [];

      this.enterpriseTenants = tenants.map(tenant => ({
        label: tenant.name,
        value: tenant.tenantId,
        data: { id: tenant.tenantId },
        checked: false,
      }));

      if (this.__isEnabled && !this.__loadingPermissions) {
        await this.__loadEnterpriseTenantPermissions();
      }
    } catch (error) {
      this.__handleError('Failed to load enterprise locations');
    } finally {
      this.__loadingTenants = false;
    }
  }

  async __loadEnterpriseAdminStatus() {
    if (!this.userId) {
      return;
    }

    try {
      const state = store.getState();
      const currentTenantId = state?.session?.item?.tenantId;

      const response = await getEnterpriseAdmin(currentTenantId, this.userId);

      const enterpriseAdminStatus = !!response.enterpriseAdmin;

      this.enterpriseAdmin = enterpriseAdminStatus;

      this.model = {
        ...this.model,
        enterpriseAdmin: enterpriseAdminStatus,
        enterpriseId: response.enterpriseId || this.model?.enterpriseId,
      };
    } catch (error) {
      this.__handleError('Failed to load enterprise admin status');
      this.enterpriseAdmin = false;
      this.model = {
        ...this.model,
        enterpriseAdmin: false,
      };
    }
  }

  async __loadEnterpriseTenantPermissions() {
    if (!this.userId || !this.__isEnabled) {
      return;
    }

    try {
      this.__loadingPermissions = true;
      const { currentTenantId } = getSessionState();

      const response = await getEnterpriseTenantPermissions(
        currentTenantId,
        this.userId,
      );

      const tenantIds = response.tenantIds || [];

      this.model = {
        ...this.model,
        enterpriseAdmin: !!this.model.enterpriseAdmin,
        enterpriseTenantPermissions: tenantIds,
      };

      this.enterpriseTenants = this.enterpriseTenants.map(tenant => ({
        ...tenant,
        checked: tenantIds.includes(tenant.value),
      }));
    } catch (error) {
      this.__handleError('Failed to load enterprise permissions');
    } finally {
      this.__loadingPermissions = false;
    }
  }

  async __deleteEnterpriseTenantPermissions() {
    try {
      const { currentTenantId } = getSessionState();
      await deleteEnterpriseTenantPermissions(currentTenantId, this.userId);
    } catch (error) {
      this.__handleError('Failed to remove enterprise permissions');
    }
  }

  __initHandlers() {
    this.__handlers = {
      change: async e => {
        if (e.name === 'enterpriseAdmin') {
          if (e.value) {
            await this.__handleEnableEnterpriseAdmin();
          } else {
            await this.__handleDisableEnterpriseAdmin();
          }
        } else if (e.name === 'enterpriseTenantPermissions') {
          this.__handleLocationAccessChange(e.value);
        } else {
          this.onChange(e);
        }
      },
    };
  }

  async __handleEnableEnterpriseAdmin() {
    const { sessionEnterpriseId } = getSessionState();
    const modelEnterpriseId = this.model?.enterpriseId;
    const enterpriseId = modelEnterpriseId || sessionEnterpriseId;

    if (enterpriseId) {
      this.__isEnabled = true;

      this.onChange({
        name: 'enterpriseAdmin',
        value: true,
        skipDismiss: true,
      });

      if (!this.__loadingTenants) {
        await this.__loadEnterpriseTenants();
      }
    } else {
      this.__isEnabled = false;
      this.requestUpdate();
    }
  }

  __handleDisableEnterpriseAdmin() {
    this.__isEnabled = false;

    this.enterpriseTenants = this.enterpriseTenants.map(tenant => ({
      ...tenant,
      checked: false,
    }));

    this.onChange({
      name: 'enterpriseAdmin',
      value: false,
      skipDismiss: true,
    });

    this.onChange({
      name: 'enterpriseTenantPermissions',
      value: [],
      skipDismiss: true,
    });
  }

  __handleLocationAccessChange(allItemsWithCheckStatus) {
    const selectedTenantIds = allItemsWithCheckStatus
      .filter(item => item.checked)
      .map(item => item.value);

    this.enterpriseTenants = allItemsWithCheckStatus;

    // Only trigger onChange for enterpriseTenantPermissions since enterpriseAdmin isn't changing
    this.onChange({
      name: 'enterpriseTenantPermissions',
      value: selectedTenantIds,
      skipDismiss: true,
    });
  }

  __updateIsEnabledFromModel(changedProps) {
    if (changedProps.has('model') && this.model) {
      const previousModel = changedProps.get('model');
      const enterpriseAdminChanged =
        previousModel?.enterpriseAdmin !== this.model.enterpriseAdmin;

      if (!previousModel || enterpriseAdminChanged) {
        if (this.model.enterpriseAdmin === false) {
          this.__isEnabled = false;
        } else if (this.model.enterpriseAdmin === true) {
          this.__isEnabled = true;
        }
      }
    }
  }

  update(changedProps) {
    const { cognitoType } = getSessionState();
    const isSupport = cognitoType === COGNITO_TYPES.SUPPORT;
    const isCurrentUserEnterpriseAdmin = !!this.model?.enterpriseAdmin;

    this.__updateIsEnabledFromModel(changedProps);

    this.__enterpriseAdminPermission =
      isSupport || isCurrentUserEnterpriseAdmin;

    super.update(changedProps);
  }

  async updated(changedProps) {
    await super.updated(changedProps);

    if (changedProps.has('userId') && this.userId && !this.__loadingTenants) {
      await this.__loadEnterpriseAdminStatus();
      await this.__loadEnterpriseTenants();
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.__loadEnterpriseAdminStatus();
  }

  static get styles() {
    return [
      css`
        .container {
          flex: 1 0 0;
          display: block;
          height: fit-content;
          width: 100%;
          padding: ${CSS_SPACING} 0;
        }

        .info {
          margin-bottom: ${CSS_SPACING};
          padding: 0 ${CSS_SPACING};
        }

        .permission {
          display: flex;
          margin-bottom: ${CSS_SPACING};
          padding: 0 ${CSS_SPACING};
        }

        .loading {
          padding: 0 ${CSS_SPACING};
          color: #666;
          font-style: italic;
        }

        .no-enterprise-message {
          padding: 0 ${CSS_SPACING};
          color: #666;
          font-style: italic;
          margin-bottom: ${CSS_SPACING};
        }

        .location-access-card {
          display: flex;
          margin-bottom: ${CSS_SPACING};
          padding: 0 ${CSS_SPACING};
        }

        .card-container {
          display: grid;
          grid-template-columns: 1fr auto;
          min-height: 60px;
          width: 100%;
          align-items: center;
          justify-content: space-between;
          padding: ${CSS_SPACING};
          border-radius: 4px;
          border: 1px solid ${CSS_COLOR_GREY_2};
          box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
        }

        .card-content {
          display: flex;
          align-items: center;
        }

        .card-icon {
          flex: 0 0 24px;
          display: flex;
          width: 28px;
          height: 28px;
          margin-right: ${CSS_SPACING};
          fill: ${CSS_COLOR_HIGHLIGHT};
        }

        .card-label-title {
          width: 100px;
          margin-right: ${CSS_SPACING};
          font-size: ${CSS_FONT_SIZE_HEADER};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          color: ${CSS_COLOR_HIGHLIGHT};
        }

        .card-label-description {
          flex: 1 0 0;
          width: auto;
          max-width: 460px;
          font-size: ${CSS_FONT_SIZE_BODY};
          margin-right: ${CSS_SPACING};
        }

        .card-select {
          flex: 1;
          min-width: 300px;
        }

        .card-select neb-select-search-multi {
          height: 50px;
          width: 100%;
        }

        .card-select neb-select-search-multi .container {
          padding-top: 0;
          width: 100%;
        }

        .card-select neb-select-search-multi .textbox {
          max-height: 40px;
          min-height: 40px;
          width: 100%;
        }
      `,
    ];
  }

  __renderInstructionalText() {
    return this.__enterpriseAdminPermission
      ? html`
          <div id="${ELEMENTS.info.id}" class="info">
            Enterprise Admin users have elevated privileges across multiple
            practices within their assigned enterprise and can manage
            enterprise-level settings and configurations.
          </div>
        `
      : '';
  }

  __renderNoEnterpriseMessage() {
    return html`
      <div class="no-enterprise-message">
        This user does not belong to an enterprise. Enterprise admin permissions
        are only available for users within an enterprise.
      </div>
    `;
  }

  __renderLocationAccessCard() {
    if (!this.__isEnabled) {
      return '';
    }

    const isLoading =
      this.__saving || this.__loadingTenants || this.__loadingPermissions;
    const description = this.__loadingPermissions
      ? 'Loading existing permissions...'
      : 'Locations this user can access within the enterprise.';

    return html`
      <div class="location-access-card">
        <div class="card-container">
          <div class="card-content">
            <neb-icon class="card-icon" icon="neb:location"></neb-icon>
            <div class="card-label-title">Location Access</div>
            <div class="card-label-description">${description}</div>
          </div>

          <div class="card-select">
            <neb-select-search-multi
              id="${ELEMENTS.locationAccess.id}"
              name="enterpriseTenantPermissions"
              placeholder="Select locations"
              displayMode="menu"
              .items="${this.enterpriseTenants}"
              .totalItems="${this.enterpriseTenants.length}"
              ?disabled="${isLoading}"
              .onChange="${this.__handlers.change}"
            ></neb-select-search-multi>
          </div>
        </div>
      </div>
    `;
  }

  __renderEnterpriseAdminToggle() {
    const { sessionEnterpriseId } = getSessionState();
    const modelEnterpriseId = this.model?.enterpriseId;
    const hasEnterpriseId = modelEnterpriseId || sessionEnterpriseId;

    if (!hasEnterpriseId) {
      return this.__renderNoEnterpriseMessage();
    }

    const description = `Users who have elevated privileges across multiple practices 
      within their assigned enterprise and can manage enterprise-level settings, 
      configurations, and cross-practice administrative functions.`;

    return html`
      <neb-card-switch
        id="${ELEMENTS.enterpriseAdmin.id}"
        class="permission permission-enterprise-admin"
        name="enterpriseAdmin"
        description="${description}"
        title="Enterprise Admin"
        icon="neb:organization"
        .active="${this.__isEnabled}"
        .layout="${this.layout}"
        .onChange="${this.__handlers.change}"
        ?disabled="${this.__saving}"
      ></neb-card-switch>
    `;
  }

  render() {
    return html`
      <div id="${ELEMENTS.container.id}" class="container">
        ${this.__renderInstructionalText()}
        ${this.__renderEnterpriseAdminToggle()}
        ${this.__renderLocationAccessCard()}
      </div>
    `;
  }
}

customElements.define('neb-enterprise-admin', NebEnterpriseAdmin);
